/**
 * "GENERATE AI SUGGESTIONS", FROM THE BROWSER'S SIDE.
 *
 * Submit a photograph, poll for the answer, stop if asked. That is the whole
 * of it, and the shape is forced by one fact: the frozen Sol V1 run took 27.6
 * to 47.0 seconds per photograph, so there is no request that can simply wait
 * for the answer.
 *
 * WHAT LEAVES THIS FILE
 *   the photograph's bytes, and nothing else
 *   the access code and the request id, as headers
 *
 * WHAT DOES NOT
 *   the customer name, the address, the saved design, the project id, any
 *   other document field, any API key. There is no key here to leak: the key
 *   lives in the functions runtime and this file never sees one.
 *
 * WHAT COMES BACK
 *   RoofProposal[] and nothing else, because the fail-closed adapter runs on
 *   the server. A response this file cannot make sense of yields no proposals
 *   rather than a partial import, and the caller's design is untouched.
 */

import type { RoofProposal } from '../types/proposal'
import {
  analysisCopyFor,
  AnalysisCopyError,
  UPLOAD_LIMIT_BYTES,
  type AnalysisCopy,
} from './analysisPhoto'

/* ------------------------------------------------------------------ types */

/** What the person sees on the button, in the order they see it. */
export type SuggestPhase = 'idle' | 'uploading' | 'analyzing' | 'ready' | 'failed' | 'cancelled'

export interface SuggestProgress {
  phase: SuggestPhase
  /** Present once the submission has been accepted. */
  requestId?: string
  /** How many times the status endpoint has been read. */
  polls?: number
}

export type SuggestOutcome =
  | { ok: true; requestId: string; proposals: RoofProposal[] }
  | { ok: false; requestId?: string; failure: string; message: string; cancelled?: boolean }

export interface SuggestOptions {
  /**
   * THE ORIGINAL PHOTO, read and never written. Its bytes are sent as they are
   * whenever they fit; when they do not, a smaller ANALYSIS COPY is made and
   * sent instead, and the original is untouched either way. See
   * lib/analysisPhoto.ts, which is the only place that distinction lives.
   */
  dataUrl: string
  /** The original's pixel size, needed to resize without distorting it. */
  photoWidth?: number
  photoHeight?: number
  /** Swappable so a test can drive the resize path without a canvas. */
  prepare?: (o: { dataUrl: string; width: number; height: number }) => Promise<AnalysisCopy>
  /** The shared code for this private trial. Not customer authentication. */
  accessCode: string
  onProgress?: (p: SuggestProgress) => void
  signal?: AbortSignal
  /* -- seams, so the whole of this runs in a test with no network and no clock */
  fetchImpl?: typeof fetch
  now?: () => number
  sleep?: (ms: number) => Promise<void>
  randomId?: () => string
  /** Stop polling after this long. Generous: the frozen run peaked at 47 s. */
  deadlineMs?: number
  /** Overridable only so a test can exercise the boundary cheaply. */
  maxUploadBytes?: number
}

/* --------------------------------------------------------------- messages */

/**
 * The server sends a message with every refusal it can classify. These are the
 * cases the server never got to answer, and they say the one thing that
 * matters either way: the design has not changed.
 */
/**
 * THE LARGEST PHOTOGRAPH THIS APP WILL UPLOAD.
 *
 * The same number as the server's MAX_IMAGE_BYTES, and it has to be, because
 * the real ceiling belongs to neither of them: Netlify buffers a request at
 * 6 MB and base64 encodes binary bodies first, so about 4.5 MB of image is all
 * that fits and a bigger one is rejected by the platform before any function
 * runs. Checking it here turns a failed five-megabyte upload and a confusing
 * platform error into an instant, readable answer.
 *
 * Deliberately NOT solved by compressing the photograph down to fit. The
 * product is reading a roof line off a real photograph, and quietly degrading
 * the input degrades the thing being sold.
 */
/** Re-exported so callers have one name for it. The limit itself is shared. */
export const MAX_UPLOAD_BYTES = UPLOAD_LIMIT_BYTES

export const CLIENT_MESSAGES = {
  offline: 'Could not reach suggestions. Nothing has changed in your design.',
  could_not_start: 'Suggestions could not be started. Nothing has changed in your design.',
  timed_out: 'That took too long, so we stopped. Nothing has changed in your design.',
  cancelled: 'Stopped. Nothing has changed in your design.',
  no_photo: 'Add a photo first, then press Generate.',
  too_large:
    'That photo is too large to send. Take it again at a smaller size, or crop it, and try Generate once more.',
} as const

/* -------------------------------------------------------------- the parts */

/**
 * 192 bits from the browser's CSPRNG, base64url - the same entropy the server
 * would have used. The id is a capability: whoever holds it can read that
 * job's result, so it is random rather than sequential, and it is minted here
 * because a background function's 202 carries no body the browser can read.
 */
export function newRequestId(): string {
  const bytes = new Uint8Array(24)
  crypto.getRandomValues(bytes)
  let s = ''
  for (const b of bytes) s += String.fromCharCode(b)
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * A data URL is base64 text; the wire wants bytes. Nothing else is read.
 *
 * The buffer is allocated explicitly so the result is a plain Uint8Array over
 * a plain ArrayBuffer, which is what fetch will accept as a body.
 */
export function decodeDataUrl(dataUrl: string): { bytes: Uint8Array<ArrayBuffer>; type: string } | null {
  const m = /^data:([^;,]+)(;base64)?,(.*)$/s.exec(dataUrl)
  if (!m) return null
  const type = m[1].toLowerCase()
  const pack = (chars: string) => {
    const out = new Uint8Array(new ArrayBuffer(chars.length))
    for (let i = 0; i < chars.length; i++) out[i] = chars.charCodeAt(i)
    return out
  }
  try {
    return { bytes: pack(m[2] ? atob(m[3]) : decodeURIComponent(m[3])), type }
  } catch {
    return null
  }
}

/**
 * BACKOFF. 1 s, then 1.5 s, then 2 s and so on up to 5 s.
 *
 * The answer never arrives before about 27 seconds, so an eager poll is pure
 * noise; a slow one leaves the person watching a spinner after the work is
 * done. This starts responsive and settles down.
 */
export function pollDelay(attempt: number): number {
  return Math.min(1000 + attempt * 500, 5000)
}

const defaultSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

/* ------------------------------------------------------------ the request */

export const SUBMIT_PATH = '/api/roof-suggestions'
export const statusPath = (id: string) => `/api/roof-suggestions/${encodeURIComponent(id)}`
export const cancelPath = (id: string) => `${statusPath(id)}/cancel`

/**
 * Tell the server to stop. Honest about what it does: the job is marked
 * cancelled so nothing can be imported afterwards, but a request already
 * running at OpenAI may still finish and still be billed.
 */
export async function cancelSuggestions(
  requestId: string,
  accessCode: string,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  try {
    await fetchImpl(cancelPath(requestId), {
      method: 'POST',
      headers: { 'x-lightsos-access': accessCode },
    })
  } catch {
    // Cancel is best effort. The browser has already stopped waiting, and the
    // job expires on its own; a failed cancel changes nothing the person sees.
  }
}

async function readJson(res: Response): Promise<Record<string, unknown> | null> {
  try {
    const v = await res.json()
    return v && typeof v === 'object' ? (v as Record<string, unknown>) : null
  } catch {
    // A proxy error page, an HTML fallback, a truncated body. Not readable,
    // therefore not a result, therefore nothing is imported.
    return null
  }
}

/* ------------------------------------------------- defense in depth */

/**
 * EVERY FIELD A PROPOSAL IS ALLOWED TO HAVE, taken from RoofProposal itself.
 *
 * The list is the contract. A field that is not on it has no meaning to this
 * app, which means either the server changed and this client did not, or
 * something between them rewrote the body - and neither is a thing to import
 * half of.
 */
const PROPOSAL_FIELDS = new Set([
  'id', 'origin', 'provider', 'promptVersion', 'schemaHash', 'createdAt', 'seq',
  'points', 'kind', 'ends', 'spans', 'geometryRev', 'status', 'confirmedAt',
  'strandId', 'confirmedRev', 'confirmedGeometry', 'confirmedFingerprint',
  'aiGeneration',
])
const END_FIELDS = new Set([
  'kind', 'open', 'weldedTo', 'sideReturnWarning', 'sideReturnDecision',
  'decidedAt', 'trimToIndex', 'trimApplied',
])
const SPAN_FIELDS = new Set(['fromIndex', 'toIndex', 'visibility', 'obstruction', 'decision', 'decidedAt'])

const END_KINDS = new Set(['endpoint', 'corner', 'peak'])
const VISIBILITIES = new Set(['clear', 'occluded', 'cannot_determine'])

const isObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v)

/** Undeclared key anywhere inside a proposal: the whole batch is refused. */
const onlyKnownKeys = (o: Record<string, unknown>, allowed: Set<string>) =>
  Object.keys(o).every((k) => allowed.has(k))

const isNormalizedPoint = (v: unknown): boolean =>
  isObject(v) &&
  onlyKnownKeys(v, new Set(['x', 'y'])) &&
  typeof v.x === 'number' &&
  typeof v.y === 'number' &&
  Number.isFinite(v.x) &&
  Number.isFinite(v.y)

function validEnd(v: unknown): boolean {
  if (!isObject(v) || !onlyKnownKeys(v, END_FIELDS)) return false
  if (!END_KINDS.has(v.kind as string)) return false
  if (typeof v.open !== 'boolean') return false
  if (typeof v.sideReturnWarning !== 'boolean') return false
  if (v.trimToIndex != null && !Number.isInteger(v.trimToIndex)) return false
  return true
}

/**
 * A SECOND VALIDATION, ON PURPOSE.
 *
 * The fail-closed adapter already ran on the server, and this repeats part of
 * its work. That is the point: the server is one process away, and this is the
 * last thing standing between a response and a person's design, so a server
 * that was changed, a proxy that rewrote a body and a cache that served the
 * wrong one all have to get past a check that runs here.
 *
 * The rule is asymmetric, deliberately:
 *
 *   ENVELOPE   unexpected fields are DISCARDED. A server may add a field to
 *              its own response without breaking every browser that has not
 *              been reloaded.
 *   PROPOSAL   anything malformed or undeclared fails the WHOLE import, every
 *              proposal in the batch included. A proposal this app does not
 *              fully understand is not a proposal it can put in a design, and
 *              importing the rest of the batch around it is precisely the
 *              partial import the whole architecture exists to prevent.
 */
export function validateProposals(list: unknown): RoofProposal[] | null {
  if (!Array.isArray(list)) return null

  const seen = new Set<string>()
  for (const p of list) {
    if (!isObject(p) || !onlyKnownKeys(p, PROPOSAL_FIELDS)) return null

    if (typeof p.id !== 'string' || !p.id) return null
    // Two proposals with one id would collide in the document, and which one
    // survived would depend on iteration order. Refuse instead.
    if (seen.has(p.id)) return null
    seen.add(p.id)

    if (p.origin !== 'ai') return null
    if (typeof p.createdAt !== 'string' || !p.createdAt) return null
    if (p.kind !== 'eave' && p.kind !== 'rake') return null
    if (p.status !== 'proposed') return null
    if (!Number.isFinite(p.geometryRev)) return null
    if (p.seq != null && !Number.isInteger(p.seq)) return null

    if (!Array.isArray(p.points) || p.points.length < 2) return null
    if (!p.points.every(isNormalizedPoint)) return null

    if (!isObject(p.ends) || !onlyKnownKeys(p.ends, new Set(['start', 'end']))) return null
    if (!validEnd(p.ends.start) || !validEnd(p.ends.end)) return null

    // Always points.length - 1, and always in order. A span list that does not
    // line up with the points is geometry this app cannot reason about.
    if (!Array.isArray(p.spans) || p.spans.length !== p.points.length - 1) return null
    for (let i = 0; i < p.spans.length; i++) {
      const span = p.spans[i]
      if (!isObject(span) || !onlyKnownKeys(span, SPAN_FIELDS)) return null
      if (span.fromIndex !== i || span.toIndex !== i + 1) return null
      if (!VISIBILITIES.has(span.visibility as string)) return null
    }

    /*
     * A proposal arriving already confirmed, or already owning a strand, would
     * be a priced run that no person ever included - the one thing this whole
     * layer exists to make impossible. The endpoint cannot hand one over.
     */
    if (p.strandId != null) return null
    if (p.status !== 'proposed') return null
    if (p.confirmedRev != null || p.confirmedGeometry != null) return null
    if (p.confirmedAt != null || p.confirmedFingerprint != null) return null
  }

  return list as RoofProposal[]
}

/** The envelope's own extra fields are ignored; the proposals are not. */
function readProposals(body: Record<string, unknown>): RoofProposal[] | null {
  return validateProposals(body.proposals)
}

/**
 * The whole flow: mint an id, post the photograph, poll, come back with
 * proposals or with a reason.
 *
 * It never retries a paid call on its own. A failure ends here and the person
 * decides whether to press Generate again, because every automatic retry is
 * another six cents and another photograph on the wire.
 */
export async function requestSuggestions(opts: SuggestOptions): Promise<SuggestOutcome> {
  const fetchImpl = opts.fetchImpl ?? fetch
  const now = opts.now ?? Date.now
  const sleep = opts.sleep ?? defaultSleep
  const deadlineMs = opts.deadlineMs ?? 150_000
  const report = (p: SuggestProgress) => opts.onProgress?.(p)

  /*
   * THE ANALYSIS COPY IS MADE HERE, AND ONLY HERE.
   *
   * If the original fits, this hands back the original's own bytes and nothing
   * has been re-encoded. If it does not, a smaller copy is made for THIS
   * REQUEST ONLY - never saved, never drawn, never returned to the caller. The
   * document's photo is an input to this and never an output.
   *
   * The old behavior was to refuse anything past the transport limit, which
   * to a contractor with a 4K phone reads as "this app does not work on my
   * photos". It still refuses when even a floor-sized copy cannot fit, because
   * sending something no model can read a roof off is worse than saying no.
   */
  let photo: AnalysisCopy
  try {
    photo = await (opts.prepare ?? analysisCopyFor)({
      dataUrl: opts.dataUrl,
      width: opts.photoWidth ?? 0,
      height: opts.photoHeight ?? 0,
    })
  } catch (err) {
    report({ phase: 'failed' })
    const message =
      err instanceof AnalysisCopyError ? err.message : CLIENT_MESSAGES.no_photo
    return {
      ok: false,
      failure: message === CLIENT_MESSAGES.no_photo ? 'bad_request' : 'image_too_large',
      message,
    }
  }

  // Belt to that pair of braces: nothing past the transport limit goes up.
  if (photo.bytes.byteLength > (opts.maxUploadBytes ?? MAX_UPLOAD_BYTES)) {
    report({ phase: 'failed' })
    return { ok: false, failure: 'image_too_large', message: CLIENT_MESSAGES.too_large }
  }

  const requestId = (opts.randomId ?? newRequestId)()
  const started = now()

  const stopped = (): SuggestOutcome => {
    report({ phase: 'cancelled', requestId })
    return { ok: false, requestId, failure: 'cancelled', message: CLIENT_MESSAGES.cancelled, cancelled: true }
  }
  if (opts.signal?.aborted) return stopped()

  /* ---- 1. the photograph goes up, once */

  report({ phase: 'uploading', requestId })
  let accepted: Response
  try {
    accepted = await fetchImpl(SUBMIT_PATH, {
      method: 'POST',
      headers: {
        'content-type': 'application/octet-stream',
        'x-image-type': photo.type,
        'x-lightsos-access': opts.accessCode,
        'x-request-id': requestId,
      },
      body: photo.bytes,
      signal: opts.signal,
    })
  } catch {
    if (opts.signal?.aborted) return stopped()
    report({ phase: 'failed', requestId })
    return { ok: false, requestId, failure: 'network', message: CLIENT_MESSAGES.offline }
  }

  /*
   * THIS RESPONSE IS WORTH READING, because the upload endpoint is
   * SYNCHRONOUS. It is not the background function - a background function
   * answers a fixed 202 whose body nobody sees - it is the ordinary function
   * that takes the photograph, and its refusals are real answers: a wrong code,
   * a file too large, a spent budget. Reading them here means the person is
   * told immediately instead of watching a spinner until the poller gives up.
   *
   * An ACCEPTED upload still tells us nothing about the reading itself, so the
   * poll below remains the only source of truth for the result.
   */
  if (!accepted.ok) {
    const body = await readJson(accepted)
    const failure = typeof body?.failure === 'string' ? body.failure : 'could_not_start'
    const message =
      typeof body?.message === 'string' && body.message ? body.message : CLIENT_MESSAGES.could_not_start
    report({ phase: 'failed', requestId })
    return { ok: false, requestId, failure, message }
  }

  /* ---- 2. poll until it is done, refused, or out of time */

  report({ phase: 'analyzing', requestId, polls: 0 })
  let attempt = 0
  let unknowns = 0

  for (;;) {
    if (opts.signal?.aborted) {
      await cancelSuggestions(requestId, opts.accessCode, fetchImpl)
      return stopped()
    }
    await sleep(pollDelay(attempt))
    if (opts.signal?.aborted) {
      await cancelSuggestions(requestId, opts.accessCode, fetchImpl)
      return stopped()
    }
    attempt++

    if (now() - started > deadlineMs) {
      await cancelSuggestions(requestId, opts.accessCode, fetchImpl)
      report({ phase: 'failed', requestId, polls: attempt })
      return { ok: false, requestId, failure: 'upstream_timeout', message: CLIENT_MESSAGES.timed_out }
    }

    let res: Response
    try {
      res = await fetchImpl(statusPath(requestId), {
        method: 'GET',
        headers: { 'x-lightsos-access': opts.accessCode },
        cache: 'no-store',
      })
    } catch {
      // One dropped poll is not a failure: the answer is still on the server
      // and reads are idempotent, so the next poll collects it.
      unknowns++
      if (unknowns > 6) {
        report({ phase: 'failed', requestId, polls: attempt })
        return { ok: false, requestId, failure: 'network', message: CLIENT_MESSAGES.offline }
      }
      continue
    }

    const body = await readJson(res)
    const state = body?.state

    if (state === 'pending') {
      unknowns = 0
      report({ phase: 'analyzing', requestId, polls: attempt })
      continue
    }

    if (state === 'done') {
      const proposals = readProposals(body as Record<string, unknown>)
      if (!proposals) {
        report({ phase: 'failed', requestId, polls: attempt })
        return {
          ok: false,
          requestId,
          failure: 'response_invalid',
          message: 'The suggestion came back unreadable, so nothing was imported.',
        }
      }
      report({ phase: 'ready', requestId, polls: attempt })
      return { ok: true, requestId, proposals }
    }

    if (state === 'failed') {
      const failure = typeof body?.failure === 'string' ? body.failure : 'response_invalid'
      const message =
        typeof body?.message === 'string' && body.message
          ? body.message
          : CLIENT_MESSAGES.could_not_start
      report({ phase: 'failed', requestId, polls: attempt })
      return { ok: false, requestId, failure, message }
    }

    if (state === 'cancelled') return stopped()

    /*
     * UNKNOWN, or a body that made no sense at all.
     *
     * The commonest cause is honest: a background function that has not yet
     * written its pending record. The other cause is a refusal that could not
     * be recorded, which is only ever a wrong access code. So this is patient
     * for a few polls and then says the one thing that is true of both.
     */
    unknowns++
    if (unknowns > 6) {
      report({ phase: 'failed', requestId, polls: attempt })
      return { ok: false, requestId, failure: 'could_not_start', message: CLIENT_MESSAGES.could_not_start }
    }
    report({ phase: 'analyzing', requestId, polls: attempt })
  }
}
